import { isTrmIntelligencePlaceholder } from './trmIntelligence.js';
import { TRM_CLIENT_FIELDS, TRM_SECTIONS, TRM_SENSITIVE_FIELDS, type TrmMedicalProvider } from './trmWorkflow.js';

const provided = (...values: unknown[]) => values.map(value => String(value ?? '').trim())
    .find(value => !isTrmIntelligencePlaceholder(value) && !/^pending$/i.test(value)) || '';
const lines = (value: string) => [...new Set(value.split('\n').map(line => line.trim()).filter(Boolean))];
const contactName = (value: string) => value.replace(/^[^:]+:\s*/, '').split('|')[0]
    .replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase();

export const TRM_CONTACT_LABELS: Record<string, string> = {
    company: 'Company', companyEmergencyLine: 'Company emergency line', clientSecurity: 'Client security',
    embassy: 'Embassy', medical: 'Medical', medevac: 'Medevac', insurance: 'Insurance emergency line',
    travelInsurer: 'Travel insurer', policyNumber: 'Policy number', coverageDates: 'Coverage dates',
    emergencyContacts: 'Other emergency contacts',
};

export function trmDashboardTrip(service: any, brief: any, seeded: any, sensitive: Record<string, string> = {}) {
    const intake = service.trmIntake || {};
    const published = Boolean(brief.publishedAt);
    const values = brief.values || {};
    const clientSection = (id: string) => Object.fromEntries(TRM_SECTIONS.find(section => section.id === id)!.fields
        .filter(field => TRM_CLIENT_FIELDS[id]?.has(field.key) && !TRM_SENSITIVE_FIELDS.has(field.key))
        .map(field => [field.key, provided(values[id]?.[field.key], seeded.values[id]?.[field.key])]));
    const profile = clientSection('s1');
    const overview = clientSection('s2');
    const countries: string[] = brief.countries?.length ? brief.countries : seeded.countries;
    const providers = countries.map((country, index): TrmMedicalProvider => {
        const provider = (intake.medicalProviders || []).find((item: TrmMedicalProvider) => item.country === country);
        return { country,
            medicalProvider: provided(values.s10?.[index]?.medicalProvider, provider?.medicalProvider),
            medevacProvider: provided(values.s10?.[index]?.medevacProvider, provider?.medevacProvider),
            medicalProviderPhone: published ? provided(sensitive[`s10.${index}.medicalProviderPhone`],
                values.s10?.[index]?.medicalProviderPhone, provider?.medicalProviderPhone) : '',
            medevacProviderPhone: published ? provided(sensitive[`s10.${index}.medevacProviderPhone`],
                values.s10?.[index]?.medevacProviderPhone, provider?.medevacProviderPhone) : '',
        };
    });
    const providerLines = (kind: 'medical' | 'medevac') => {
        const directory = published ? lines(provided(sensitive[`s12.${kind}Line`], values.s12?.[`${kind}Line`])) : [];
        for (const provider of providers) {
            const name = provider[`${kind}Provider`];
            const phone = provider[`${kind}ProviderPhone`];
            if ((name || phone) && !directory.some(line => line.toLowerCase().startsWith(`${provider.country.toLowerCase()}:`)))
                directory.push(`${provider.country}: ${[name, phone].filter(Boolean).join(' | ')}`);
        }
        return directory.join('\n');
    };
    const emergencyContacts = published ? lines(provided(sensitive['s12.emergencyContacts'], values.s12?.emergencyContacts,
        (intake.emergencyContacts || []).map((contact: any) =>
            `${contact.role}: ${[contact.name, contact.phone, contact.email].filter(Boolean).join(' | ')}`).join('\n'))) : [];
    const security = provided(values.s12?.clientContact, values.s9?.clientSecurityContact);
    const securityContacts = emergencyContacts.filter(contact => security ? contactName(contact) === contactName(security)
        : /\b(?:security|gsoc|soc)\b/i.test(contact));
    const securityDetails = security && securityContacts.length === 1 && !security.includes('|')
        ? [security, ...securityContacts[0].split('|').slice(1).map(part => part.trim())].join(' | ') : security;
    const privateContact = (key: string, ...fallback: unknown[]) => published
        ? provided(sensitive[`s12.${key}`], values.s12?.[key], ...fallback) : '';
    const contacts = {
        company: profile.travellerOrganization,
        companyEmergencyLine: provided(values.s12?.companyLine, values.s9?.companyEmergencyLine),
        clientSecurity: securityDetails || securityContacts.join('\n'),
        embassy: provided(values.s12?.embassy), medical: providerLines('medical'), medevac: providerLines('medevac'),
        insurance: privateContact('insuranceLine', sensitive['s9.insuranceLine'], values.s9?.insuranceLine,
            intake.travelInsurance?.emergencyLine),
        travelInsurer: privateContact('travelInsurer', intake.travelInsurance?.insurer),
        policyNumber: privateContact('policyNumber', intake.travelInsurance?.policyNumber),
        coverageDates: privateContact('coverageDates', [intake.travelInsurance?.coverageStart,
            intake.travelInsurance?.coverageEnd].filter(Boolean).join(' to ')),
        emergencyContacts: emergencyContacts.filter(contact => !securityContacts.includes(contact)).join('\n'),
    };
    return { profile, overview, travellerClass: profile.travellerClass, purpose: overview.tripPurpose,
        departureAt: overview.departureAt, returnAt: overview.returnAt, countries,
        legs: brief.legs?.length ? brief.legs : seeded.legs,
        accommodations: brief.accommodations?.length ? brief.accommodations : seeded.accommodations,
        locations: service.trmResearch?.locations || [], providers, contacts };
}

export type TrmDashboardTrip = ReturnType<typeof trmDashboardTrip>;
